import { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CartSummary from '../components/cart/CartSummary';
import CartItemsList from '../components/cart/CartItemsList';
import removeItemFromCart from '../hooks/cart/removeItemFromCart';
import addCoupon from '../hooks/coupon/addCoupon';
import getCouponId from '../hooks/coupon/getCouponId';
import CartContext, { useCart } from '../contexts/CartContext';
import ActionConfirmation from '../components/elements/ActionConfirmation';
import { useNavigate } from 'react-router-dom';
import { getReturnToForCart } from '../utils/navigationReturnTo';
import type { ActionConfirmationVariant } from '../types';
import createOrder from '../hooks/cart/createOrder';
import { KEY_COUPON_LOCATION_ID_ALL, KEY_ORDER_TYPE_DEPOSIT, KEY_ORDER_TYPE_FULL } from '../constants';
import { useUser } from '../contexts/UserContext';
import removeAllItemsFromCart from '../hooks/cart/removeAllItemsFromCart';
import { CartItem, CartItemType, OrderErrors } from '../types';
import { getChildrenInMultipleEventsAtTheSameTime } from '../utils';
declare const window: any;
import { useScrollToConfirmation } from '../hooks/useScrollToConfirmation';

const CartPage = () => {
  const { applyCoupon, setOrderType, fetchCart } = useCart();
  const { cartItems } = useContext(CartContext) as unknown as { cartItems: CartItemType };
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    visible: boolean;
    variant: ActionConfirmationVariant;
    message: string;
    linkLabel?: string;
    linkOnClick?: () => void;
  }>({
    visible: false,
    variant: 'info',
    message: '',
  });

  const [multipleEventsConfirmModa, setMultipleEventsConfirmModa] = useState<{
    isOpen: boolean;
    type: string;
    data: any;
  }>({ isOpen: false, type: KEY_ORDER_TYPE_FULL, data: [] });

  const [expirationModal, setExpirationModal] = useState<{
    open: boolean;
    expiredItems: Array<{ eventName: string; childName: string; itemId: number }>;
  }>({ open: false, expiredItems: [] });

  const handleMultipleEventsConfirmModaOpen = (type: string) => {
    let children = getChildrenInMultipleEventsAtTheSameTime(cartItems.data);
    if (Object.keys(children).length === 0) {
      handleCreateOrder(type);
      return;
    }
    setMultipleEventsConfirmModa({ isOpen: true, type, data: Object.values(children) });
  };
  const handleMultipleEventsConfirmModaClose = () =>
    setMultipleEventsConfirmModa({ ...multipleEventsConfirmModa, isOpen: false });
  const handleMultipleEventsConfirmModaConfirm = () => {
    setMultipleEventsConfirmModa({ ...multipleEventsConfirmModa, isOpen: false });
    handleCreateOrder(multipleEventsConfirmModa.type);
  };

  const handleDismissConfirmation = () => {
    setConfirmation((c) => ({ ...c, visible: false, linkLabel: undefined, linkOnClick: undefined }));
  };

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user]);

  useScrollToConfirmation(confirmation.visible, confirmation.message, 'cart-confirmation');

  useEffect(() => {
    const initFetchCart = async () => {
      setLoading(true);
      try {
        const responseCart = await fetchCart();
        if (!responseCart) {
          setConfirmation({ visible: true, variant: 'error', message: 'Failed to retrieve cart.' });
        }
      } catch (error) {
        setConfirmation({ visible: true, variant: 'error', message: 'Failed to retrieve cart.' });
      } finally {
        setLoading(false);
      }
    };

    initFetchCart();
  }, []);

  useEffect(() => {
    if (cartItems) {
      // Check for expired items first
      const hasExpiredItems = checkForExpiredItems();

      // Only show other snackbars if there are no expired items (modal handles that)
      if (!hasExpiredItems) {
        if (cartItems.invalidCoupons && cartItems.invalidCoupons.length > 0) {
          setConfirmation({ visible: true, variant: 'info', message: 'A coupon expired.' });
        }
        if (cartItems.invalidItems && cartItems.invalidItems.length > 0) {
          setConfirmation({
            visible: true,
            variant: 'info',
            message: 'Some item(s) have been removed from your cart because they are no longer available.',
          });
        }
        if (cartItems.invalidMessages && cartItems.invalidMessages.length > 0) {
          setConfirmation({ visible: true, variant: 'error', message: cartItems.invalidMessages.join('; ') });
        }
        if (cartItems.cartMessages && cartItems.cartMessages.length > 0) {
          setConfirmation({ visible: true, variant: 'info', message: cartItems.cartMessages.join('; ') });
        }
      }
    }
  }, [cartItems]);

  const handleItemExpired = (itemId: number, eventName: string, childName: string) => {
    // Add this item to expired list or create new modal state
    setExpirationModal((prev) => {
      // If modal is already open, add to existing list
      if (prev.open) {
        const alreadyListed = prev.expiredItems.some((item) => item.itemId === itemId);
        if (alreadyListed) return prev;

        return {
          open: true,
          expiredItems: [...prev.expiredItems, { itemId, eventName, childName }],
        };
      }

      // Open modal with this item
      return {
        open: true,
        expiredItems: [{ itemId, eventName, childName }],
      };
    });
  };

  const checkForExpiredItems = () => {
    if (!cartItems.data) return false;

    const expired = cartItems.data.filter((item: any) => {
      const heldUntil = new Date(item.heldUntil).getTime();
      const now = Date.now();
      return heldUntil <= now;
    });

    if (expired.length > 0) {
      setExpirationModal({
        open: true,
        expiredItems: expired.map((item: any) => ({
          eventName: item.item?.event?.name || 'Event',
          childName: `${item.child?.firstName} ${item.child?.lastName}`,
          itemId: item.id,
        })),
      });
      return true;
    }

    return false;
  };

  const handleExpiredItemOk = async () => {
    setLoading(true);

    // Remove all expired items from the cart
    for (const expiredItem of expirationModal.expiredItems) {
      try {
        await removeItemFromCart(expiredItem.itemId);
        // #region agent log
        // Debug logging disabled to improve performance
        // fetch('http://127.0.0.1:7242/ingest/b1326ef0-6da0-421a-90e2-7cb69d8bc229',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Cart.tsx:138',message:'Item removal result',data:{itemId:expiredItem.itemId},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'EXPIRE'})}).catch(()=>{});
        // #endregion
      } catch (error) {
        console.error('Error removing expired item:', error);
      }
    }

    // Close modal and refresh cart
    setExpirationModal({ open: false, expiredItems: [] });
    await fetchCart();
    setLoading(false);
  };

  const handleRemoveItemFromCart = async (pricingId: number) => {
    setLoading(true);
    try {
      const success = await removeItemFromCart(pricingId);
      if (success) {
        await fetchCart();
        setConfirmation({ visible: true, variant: 'success', message: 'Item removed from cart.' });
      } else {
        setConfirmation({ visible: true, variant: 'error', message: 'Failed to remove item from cart.' });
      }
    } catch (error) {
      setConfirmation({ visible: true, variant: 'error', message: 'Failed to remove item from cart.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAllItemsFromCart = async () => {
    setLoading(true);
    try {
      const removeAllItemsFromCartResponse = await removeAllItemsFromCart();
      if (removeAllItemsFromCartResponse) {
        await fetchCart();
        setConfirmation({ visible: true, variant: 'success', message: 'Cart cleared successfully.' });
      } else {
        setConfirmation({ visible: true, variant: 'error', message: 'Failed to remove all items from cart.' });
      }
    } catch (error) {
      setConfirmation({
        visible: true,
        variant: 'error',
        message: 'An error occurred while deleting all items from cart.',
      });
    } finally {
      setLoading(false);
    }
  };

  const applyCouponCode = async (couponCode: string) => {
    try {
      const couponResponse = await getCouponId(couponCode);

      if (couponResponse.success) {
        const addCouponResponse = await addCoupon(couponCode);

        if (addCouponResponse.success) {
          setConfirmation({ visible: true, variant: 'success', message: 'Coupon applied successfully.' });
          if (couponResponse.data) {
            applyCoupon({
              code: couponResponse.data.id,
              value: couponResponse.data.amount,
              type: couponResponse.data.type,
              locationId: couponResponse.data.locationId,
              locationName:
                couponResponse.data.locationId == KEY_COUPON_LOCATION_ID_ALL
                  ? ''
                  : couponResponse.data.location?.name ?? '',
            });
          }

          await fetchCart();
          return true;
        } else {
          setConfirmation({
            visible: true,
            variant: 'error',
            message: addCouponResponse.message || 'Failed to apply coupon.',
          });
          return false;
        }
      } else {
        setConfirmation({ visible: true, variant: 'error', message: 'Coupon not found.' });
        return false;
      }
    } catch (error) {
      setConfirmation({ visible: true, variant: 'error', message: 'An error occurred while applying the coupon.' });
      return false;
    }
  };

  const handleCreateOrder = async (orderType: string) => {
    setButtonDisabled(true);
    setOrderType(orderType);
    const orderResponse = await createOrder();

    if (orderResponse.success) {
      if (orderResponse.message) {
        setConfirmation({ visible: true, variant: 'info', message: orderResponse.message });
        await new Promise((resolve) => setTimeout(resolve, 6000));
        await fetchCart();
      }
      setButtonDisabled(false);
      navigate('/checkout');
    } else {
      setButtonDisabled(false);
      if (orderResponse?.message === OrderErrors.DUPLICATE_CHARGE_PREVENTED)
        setConfirmation({
          visible: true,
          variant: 'error',
          message:
            'Cart contains items you have already purchased. Please contact us at support@kindertales.com or use the chat bubble.',
        });
      else
        setConfirmation({
          visible: true,
          variant: 'error',
          message: orderResponse.message || 'An error occurred while creating order.',
        });
      return false;
    }
  };

  return (
    <div className="event-page p-5 mb-20">
      <Container maxWidth="xl" sx={{ pt: 3.5 }}>
        {/* Back Button and Page Title */}
        <Box
          sx={{
            position: 'relative',
            mb: 3.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 },
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(getReturnToForCart())}
            aria-label="Back to shopping"
            sx={{
              position: { xs: 'static', sm: 'absolute' },
              left: { sm: 0 },
              top: { sm: '50%' },
              transform: { xs: 'none', sm: 'translateY(-50%)' },
              alignSelf: { xs: 'flex-start', sm: 'auto' },
              order: { xs: 0, sm: 0 },
            }}
          >
            Back to shopping
          </Button>
          <Typography
            variant="h4"
            align="center"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.6rem', sm: '2rem' },
              textAlign: 'center',
              order: { xs: 1, sm: 0 },
            }}
          >
            Your Cart{' '}
            {loading
              ? ''
              : cartItems.data
                ? `(${cartItems.data.length} ${cartItems.data.length === 1 ? 'item' : 'items'})`
                : ''}
          </Typography>
        </Box>

        <ActionConfirmation
          id="cart-confirmation"
          visible={confirmation.visible}
          variant={confirmation.variant}
          message={confirmation.message}
          onDismiss={handleDismissConfirmation}
          linkLabel={confirmation.linkLabel}
          linkOnClick={confirmation.linkOnClick}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {/* Left Pane - Cart Items */}
            <Grid item xs={12} lg={8}>
              <CartItemsList
                items={cartItems.data}
                onRemoveItem={handleRemoveItemFromCart}
                onRemoveAll={handleRemoveAllItemsFromCart}
                onItemExpired={handleItemExpired}
                loading={loading}
              />
            </Grid>

            {/* Right Pane - Payment Summary - Always show */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ position: { lg: 'sticky' }, top: 20 }}>
                <CartSummary
                  applyCouponCode={applyCouponCode}
                  readonly={false}
                  checkout={false}
                  onPayDeposit={
                    cartItems && cartItems.data && cartItems.data.length > 0
                      ? () => handleMultipleEventsConfirmModaOpen(KEY_ORDER_TYPE_DEPOSIT)
                      : undefined
                  }
                  onPayFull={
                    cartItems && cartItems.data && cartItems.data.length > 0
                      ? () => handleMultipleEventsConfirmModaOpen(KEY_ORDER_TYPE_FULL)
                      : undefined
                  }
                  buttonDisabled={buttonDisabled}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>
      <Dialog
        open={multipleEventsConfirmModa.isOpen}
        onClose={handleMultipleEventsConfirmModaClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          id="modal-title"
          sx={{
            textAlign: 'center',
            pb: 2,
            pt: 3,
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#1a1a1a',
          }}
        >
          Potential Double Booking
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Box
            sx={{
              p: 2,
              backgroundColor: '#FFF3E0',
              borderRadius: '8px',
              border: '1px solid #FFE0B2',
              mb: 2,
            }}
          >
            <DialogContentText
              id="modal-description"
              sx={{
                color: '#E65100',
                fontSize: '1rem',
                lineHeight: 1.6,
                m: 0,
                mb: 2,
              }}
            >
              This child is already registered for another event that overlaps with the selected dates. You may proceed,
              but please note this could result in a double booking.
            </DialogContentText>
            {multipleEventsConfirmModa?.data?.length &&
              multipleEventsConfirmModa.data.map((cartItems: CartItem[]) => (
                <Box key={cartItems[0].child.id} sx={{ mb: 1, pl: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.95rem', color: '#E65100' }}>
                    • Child:{' '}
                    <strong>
                      {cartItems[0].child.firstName} {cartItems[0].child.lastName}
                    </strong>
                  </Typography>
                </Box>
              ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
          <Button
            onClick={handleMultipleEventsConfirmModaClose}
            variant="outlined"
            sx={{
              borderColor: '#666',
              color: '#666',
              px: 3,
              py: 1,
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              borderWidth: '2px',
              minWidth: '120px',
              '&:hover': {
                borderWidth: '2px',
                borderColor: '#333',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMultipleEventsConfirmModaConfirm}
            sx={{
              backgroundColor: '#297AB0',
              color: 'white',
              px: 3,
              py: 1,
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              minWidth: '120px',
              '&:hover': {
                backgroundColor: '#1f5d85',
              },
            }}
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expiration Modal */}
      <Dialog
        open={expirationModal.open}
        onClose={() => {}} // Prevent closing without clicking OK
        aria-labelledby="expiration-modal-title"
        aria-describedby="expiration-modal-description"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          id="expiration-modal-title"
          sx={{
            textAlign: 'center',
            pb: 2,
            pt: 3,
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#1a1a1a',
          }}
        >
          Reservation Expired
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Box
            sx={{
              p: 2,
              backgroundColor: '#FFEBEE',
              borderRadius: '8px',
              border: '1px solid #FFCDD2',
              mb: 2,
            }}
          >
            <DialogContentText
              sx={{
                color: '#C62828',
                fontSize: '1rem',
                lineHeight: 1.6,
                m: 0,
                mb: 2,
                fontWeight: 500,
              }}
            >
              The following item(s) have expired and will be removed from your cart:
            </DialogContentText>
            {expirationModal.expiredItems.map((item, idx) => (
              <Box key={idx} sx={{ mb: 1, pl: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.95rem', color: '#C62828' }}>
                  • <strong>{item.eventName}</strong> for {item.childName}
                </Typography>
              </Box>
            ))}
          </Box>
          <DialogContentText
            sx={{
              color: '#666',
              fontSize: '0.95rem',
              mb: 2,
              textAlign: 'center',
            }}
          >
            Please add the item(s) to your cart again to reserve a spot.
          </DialogContentText>
          <Box
            sx={{
              p: 1.5,
              backgroundColor: '#F5F5F5',
              borderRadius: '8px',
              border: '1px solid #E0E0E0',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#666', fontStyle: 'italic', fontSize: '0.85rem', display: 'block', lineHeight: 1.5 }}
            >
              Reminder: Cart reservations are held for a limited time to ensure fair access for all customers. Items
              that are not checked out before the timer expires will be automatically removed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleExpiredItemOk}
            sx={{
              backgroundColor: '#297AB0',
              color: 'white',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#1f5d85',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CartPage;
